/**
 * Service for interacting with sleep plan API endpoints
 */
import { logger } from '../utils/logger';

export const sleepPlanService = {
  /**
   * Get the latest sleep plan for a specific baby profile
   * @param {string} token - The user's authentication token
   * @param {string} profileId - The ID of the baby profile
   * @returns {Promise<Object>} - A promise that resolves to the sleep plan data
   */
  async getSleepPlan(token, profileId) {
    try {
      const apiUrl = process.env.REACT_APP_BABY_PROFILES_API_URL;
      if (!apiUrl) {
        throw new Error('API URL not found in environment variables');
      }

      const response = await fetch(`${apiUrl}/sleep-plans/${profileId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No sleep plan found for this profile');
        }
        const errorText = await response.text();
        throw new Error(`Failed to fetch sleep plan: ${response.status} ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error fetching sleep plan:', error);
      throw error;
    }
  },

  /**
   * Format the sleep plan data for display
   * @param {Object} sleepPlanData - The raw sleep plan data from the API
   * @returns {Object} - Formatted sleep plan data ready for display
   */
  formatSleepPlanForDisplay(sleepPlanData) {
    // Extract the sleep plan content
    const sleepPlan = sleepPlanData.sleepPlan;
    const profile = sleepPlanData.profile;
    
    // Parse the content if it's a string (it might be stored as JSON string in the database)
    let parsedContent;
    try {
      parsedContent = typeof sleepPlan.content === 'string' 
        ? JSON.parse(sleepPlan.content) 
        : sleepPlan.content;
    } catch (error) {
      logger.error('Error parsing sleep plan content:', error);
      // If parsing fails, use the content as is (it might be a formatted string)
      parsedContent = {
        rawContent: sleepPlan.content
      };
    }
    
    // Return a structured object with all the necessary data
    return {
      planId: sleepPlan.planId,
      profileId: sleepPlan.profileId,
      version: sleepPlan.version,
      status: sleepPlan.status,
      createdAt: sleepPlan.createdAt,
      updatedAt: sleepPlan.updatedAt,
      profile: {
        profileId: profile.profileId,
        babyName: profile.babyName,
        dateOfBirth: profile.dateOfBirth,
        gender: profile.gender
      },
      content: parsedContent
    };
  }
};
